import {
  DeleteOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { ScopeButton } from '@monorepo/shared-lib';
import {
  Button,
  Form,
  Modal,
  Select,
  Space,
  Spin,
  Tooltip,
  Upload,
} from 'antd';
import {
  buildMembersFromGroupedConnections,
  GroupConnectedEntitiesList,
} from 'components/CreateGroup/GroupConnectedEntitiesList';
import { FormInput, FormTextArea } from 'components/Custom/inputs';
import { ScopeTabs } from 'components/Custom/tabs';
import { LabelWrapper } from 'components/Custom/labels';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { FormSelect } from 'components/Custom/selects';
import { BoldNameSpan } from 'components/Custom/spans';
import { MessageStates } from 'containers/MessageBox';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { Provider } from 'store/api/enums';
import { turnMessageOn } from 'store/app/actions';
import type { MessageData } from 'store/app/types';
import {
  fetchCountriesExternalDataRequest,
  fetchCountriesRequest,
  fetchCurrenciesRequest,
} from 'store/common/actions';
import { Country } from 'store/common/models';
import {
  getCommonCountries,
  getCommonCurrencies,
  getCountriesExternalData,
  getCurrenciesLoaded,
} from 'store/common/selectors';
import {
  deleteOrganisationAttachmentRequest,
  uploadOrganisationAttachmentRequest,
} from 'store/organisation/actions';
import { EditOrganisationParams } from 'store/organisation/models';
import {
  getOrgLoading,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import styled from 'styled-components';
import { CountryFlagImage } from 'utils/render-countries-codes';
import {
  ORGANISATION_ATTACHMENT_ACCEPT,
  validateOrganisationAttachment,
} from 'utils/safe-upload';
import {
  isOrganisationNotesWithinLimit,
  ORGANISATION_NOTES_MAX_LENGTH,
} from './organisation-notes-limits';

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 20px !important;
  }

  .ant-modal-header {
    padding: 25px 24px 12px;
    border-bottom: none;
  }

  .ant-modal-body {
    padding: 12px 32px 32px;
  }

  .ant-modal-title {
    font-family: Inter;
    font-size: 18px;
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
    text-align: center;
  }

  .ant-upload-list-item:hover {
    background-color: transparent !important;
  }

  .ant-upload-list-item-actions {
    opacity: 1 !important;
    display: inline-block !important;
  }

  .ant-upload-list-item .ant-upload-list-item-actions {
    opacity: 1 !important;
  }

  .ant-upload-list-item-action {
    opacity: 1 !important;
  }
`;

const EditDetailsTabsContainer = styled.div`
  .ant-tabs {
    margin-bottom: 0;
  }

  .ant-tabs-nav {
    margin-bottom: 0;
    border-bottom: 1px solid var(--color-border, #dbeae3);
  }

  .ant-tabs-content-holder {
    margin-top: 35px;
  }

  .ant-tabs-content {
    width: 100%;
  }

  .ant-tabs-tabpane {
    width: 100%;
  }
`;

interface CardInfosProps {
  friendlyName: string;
  organisationId: string;
  editOrganisationName: (orgValues: EditOrganisationParams) => void;
  onClose: () => void;
  isAddMode?: boolean;
  /** When in add mode, indicates which provider (Excel vs Receipts) for conditional UI. */
  addModeProvider?: Provider;
}
export const EditFriendlyName: React.FC<CardInfosProps> = ({
  friendlyName,
  organisationId,
  editOrganisationName,
  onClose,
  isAddMode = false,
  addModeProvider,
}) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const dispatch = useDispatch();
  const organisations = useSelector(getOrgOrganisations);
  const countries = useSelector(getCommonCountries);
  const loading = useSelector(getOrgLoading);
  const countriesExternalData = useSelector(getCountriesExternalData);
  const currencies = useSelector(getCommonCurrencies);
  const currenciesLoaded = useSelector(getCurrenciesLoaded);
  const isExcelOrganisation = isAddMode;
  const [form] = Form.useForm();
  const [activeTabKey, setActiveTabKey] = React.useState('basic');

  // Get the specific organization from the store using organisationId
  const currentOrganisation = React.useMemo(
    () => organisations?.find((org) => org.uuid === organisationId),
    [organisations, organisationId]
  );

  const isReceiptsEntity = isAddMode
    ? addModeProvider === Provider.RECEIPTS
    : currentOrganisation?.providerName === Provider.RECEIPTS;

  // Receipts entities require "Additional Notes" only when editing an existing entity.
  const isReceiptsNotesRequired = !isAddMode && isReceiptsEntity;

  const isGroupOrganisation = React.useMemo(
    () => !isAddMode && currentOrganisation?.providerName === Provider.GROUP,
    [isAddMode, currentOrganisation?.providerName]
  );

  const groupDetailMembers = React.useMemo(
    () =>
      buildMembersFromGroupedConnections(
        currentOrganisation?.groupedConnections,
        organisations
      ),
    [currentOrganisation?.groupedConnections, organisations]
  );

  const organisationDetails = currentOrganisation?.organisationDetails || {
    attachments: [],
  };

  const editOrganisationParams: EditOrganisationParams =
    organisationDetails as EditOrganisationParams;
  const [orgValues, setOrgValues] = React.useState<EditOrganisationParams>({
    newName: friendlyName,
    ...editOrganisationParams,
    entityName: currentOrganisation?.legalName,
    currency: currentOrganisation?.currency,
    taxNumber: currentOrganisation?.taxNumber,
    country: currentOrganisation?.country,
  });

  React.useEffect(() => {
    if (isGroupOrganisation) {
      setActiveTabKey('group-details');
    } else {
      setActiveTabKey('basic');
    }
  }, [organisationId, isGroupOrganisation]);

  // Fetch countries if not already loaded
  React.useEffect(() => {
    if (!countries || countries.length === 0) {
      dispatch(fetchCountriesRequest());
    }
  }, [dispatch, countries]);

  // Fetch flag data if not already loaded
  React.useEffect(() => {
    if (
      countries &&
      countries.length > 0 &&
      (!countriesExternalData || countriesExternalData.length === 0)
    ) {
      dispatch(fetchCountriesExternalDataRequest());
    }
  }, [dispatch, countries, countriesExternalData]);

  // Fetch currencies if not already loaded. Empty `[]` is a valid API result.
  React.useEffect(() => {
    if (currenciesLoaded) return;
    dispatch(fetchCurrenciesRequest());
  }, [dispatch, currenciesLoaded]);

  // Attachment upload state
  const [isUploadModalVisible, setIsUploadModalVisible] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [fileDescription, setFileDescription] = React.useState('');

  type FormFieldConfig = {
    key: string;
    translationKey: string;
    helpTextKey?: string;
    disabled: boolean;
    required?: boolean;
  };

  const mainFields: FormFieldConfig[] = [
    {
      key: 'entityName',
      translationKey: 'organisation.company-name',
      disabled: !isExcelOrganisation,
      required: isExcelOrganisation,
    },
    {
      key: 'newName',
      translationKey: 'organisation.friendly-name',
      helpTextKey: 'organisation.friendly-name-help',
      disabled: false,
      required: true,
    },
    {
      key: 'currency',
      translationKey: 'common.currency',
      disabled: !isExcelOrganisation,
      required: isExcelOrganisation,
    },
    {
      key: 'taxNumber',
      translationKey: 'organisation.tax-number',
      helpTextKey: 'organisation.tax-number-help',
      disabled: !isExcelOrganisation,
      required: isExcelOrganisation,
    },
    {
      key: 'country',
      translationKey: 'common.country',
      disabled: !isExcelOrganisation,
      required: isExcelOrganisation,
    },
  ];

  const moreDetailsFields: FormFieldConfig[] = [
    {
      key: 'alternateName',
      translationKey: 'organisation.alternate-name',
      helpTextKey: 'organisation.alternate-name-help',
      disabled: false,
    },
    {
      key: 'code',
      translationKey: 'organisation.code',
      helpTextKey: 'organisation.code-help',
      disabled: false,
    },
    {
      key: 'contactPersonName',
      translationKey: 'organisation.contact-person-name',
      helpTextKey: 'organisation.contact-person-name-help',
      disabled: false,
    },
    {
      key: 'contactPersonEmail',
      translationKey: 'organisation.contact-person-email',
      disabled: false,
    },
    {
      key: 'contactPersonTelephone',
      translationKey: 'organisation.contact-person-telephone',
      disabled: false,
    },
    {
      key: 'registeredAddress',
      translationKey: 'organisation.registered-address',
      disabled: false,
    },
  ];

  const renderFormField = (field: FormFieldConfig) => (
    <div key={field.key}>
      <LabelWrapper>
        <span
          className="label-text"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {t(field.translationKey)}
          {field.required && <span style={{ color: '#D13131' }}> *</span>}
          {field.helpTextKey && (
            <Tooltip title={t(field.helpTextKey)}>
              <QuestionCircleOutlined
                style={{
                  fontSize: 15,
                  color: 'var(--color-text-muted, #a9b8b9)',
                  cursor: 'pointer',
                  marginBottom: -3,
                }}
              />
            </Tooltip>
          )}
        </span>
      </LabelWrapper>
      <Form.Item
        name={field.key}
        rules={
          field.required
            ? [{ required: true, message: t('common.required-field') }]
            : undefined
        }
        style={{ marginBottom: 0 }}
      >
        {field.key === 'country' ? (
          <FormSelect
            width={'100%'}
            height="40px"
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            bordercolor={field.disabled ? '#cccccc' : 'var(--color-border, #dbeae3)'}
            disabled={field.disabled}
            value={orgValues?.country}
            onChange={(val) => handleFieldChange('country', val as string)}
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            className={field.disabled ? 'disabled-select' : ''}
          >
            {countries?.map((c) => (
              <Select.Option key={c.iso} value={c.iso} label={c.name}>
                {renderCountryWithFlag(c)}
              </Select.Option>
            ))}
          </FormSelect>
        ) : field.key === 'currency' ? (
          <FormSelect
            width={'100%'}
            height="40px"
            bgcolor="var(--color-surface-raised, #FFFFFF)"
            bordercolor={field.disabled ? '#cccccc' : 'var(--color-border, #dbeae3)'}
            disabled={field.disabled}
            value={orgValues?.currency}
            onChange={(val) => handleFieldChange('currency', val as string)}
            showSearch
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
            className={field.disabled ? 'disabled-select' : ''}
          >
            {currencies
              ?.slice()
              .sort((a, b) => a.localeCompare(b))
              .map((currency) => (
                <Select.Option key={currency} value={currency} label={currency}>
                  {currency}
                </Select.Option>
              ))}
          </FormSelect>
        ) : (
          <FormInput
            width={'100%'}
            style={{ height: '40px' }}
            disabled={field.disabled}
            value={orgValues?.[field.key as keyof EditOrganisationParams] ?? ''}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
          />
        )}
      </Form.Item>
    </div>
  );

  const handleFieldChange = (key: string, value: string) => {
    const next = { ...orgValues, [key]: value };
    // In add-new-entity flow, copy entity name to friendly name when empty or when it still matches entity name (keep in sync until user edits friendly name)
    if (
      isExcelOrganisation &&
      key === 'entityName' &&
      (!orgValues.newName?.trim() || orgValues.newName === orgValues.entityName)
    ) {
      next.newName = value;
    }
    setOrgValues(next);
    // Keep Form in sync so Form.Item displays the updated values (e.g. copied friendly name)
    form.setFieldsValue(next);
  };

  const renderCountryWithFlag = (country: Country): React.ReactNode => {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CountryFlagImage
          src={country.flag}
          alt={country.name}
          cca2={country.iso ?? ''}
        />
        <span style={{ fontWeight: 'normal', lineHeight: '16px' }}>
          {localizeCountry(country.iso, country.name)}
        </span>
      </div>
    );
  };

  const handleAddAttachment = () => {
    setIsUploadModalVisible(true);
    setSelectedFile(null);
    setFileDescription('');
  };

  const handleUploadModalClose = () => {
    setIsUploadModalVisible(false);
    setSelectedFile(null);
    setFileDescription('');
  };

  const handleFileChange = (file: File) => {
    const validation = validateOrganisationAttachment(file);
    if (!validation.ok) {
      dispatch(
        turnMessageOn({
          title: t('common.unsupported-file-type-title'),
          description: validation.message,
          type: MessageStates.WARNING,
        })
      );
      return false;
    }
    setSelectedFile(file);
    return false; // Prevent auto upload
  };

  const handleUploadConfirm = () => {
    if (selectedFile && fileDescription.trim()) {
      dispatch(
        uploadOrganisationAttachmentRequest({
          organisationId,
          description: fileDescription,
          file: selectedFile,
        })
      );
      handleUploadModalClose();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <>
      <style>
        {`
          .disabled-select .ant-select-selector {
            background-color: rgba(0, 0, 0, 0.04) !important;
            cursor: not-allowed !important;
            min-height: 40px !important;
            height: 40px !important;
            display: flex !important;
            align-items: center !important;
            border: 1px solid var(--color-border, #dbeae3) !important;
            border-radius: 0 !important;
          }
          
          .disabled-select .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.25) !important;
            font-weight: 400 !important;
          }
          .disabled-select .ant-select-selection-item span {
            color: rgba(0, 0, 0, 0.25) !important;
            font-weight: 400 !important;
          }
          
          .disabled-select .ant-select-selection-item img {
            opacity: 0.3 !important;
          }

          .edit-details-notes-field .ant-input {
            width: 100% !important;
            min-height: 200px !important;
            height: 200px !important;
            resize: none !important;
            border: 1px solid var(--color-border, #dbeae3) !important;
            border-radius: 0 !important;
          }
          .edit-details-notes-field .ant-input:hover,
          .edit-details-notes-field .ant-input:focus {
            border-color: var(--color-primary, #41aaaf) !important;
            box-shadow: none !important;
          }

          .edit-details-notes-field .notes-label-optional {
            font-size: 12px;
            color: #8c8c8c;
            font-weight: 400;
          }

          .edit-friendly-name-form .ant-select {
            min-height: 40px !important;
            border-radius: 0 !important;
            border: none !important;
          }
          .edit-friendly-name-form .ant-select .ant-select-selector {
            min-height: 40px !important;
            height: 40px !important;
            display: flex !important;
            align-items: center !important;
            box-sizing: border-box !important;
            border: 1px solid var(--color-border, #dbeae3) !important;
            border-radius: 0 !important;
          }
          .edit-friendly-name-form .ant-select:hover .ant-select-selector,
          .edit-friendly-name-form .ant-select.ant-select-focused .ant-select-selector,
          .edit-friendly-name-form .ant-select.ant-select-open .ant-select-selector {
            border-color: var(--color-primary, #41aaaf) !important;
          }
          
        `}
      </style>
      <Space
        direction="vertical"
        style={{
          width: '100%',
          //rowGap: 20,
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={orgValues}
          className="edit-friendly-name-form"
        >
          <EditDetailsTabsContainer>
            <ScopeTabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              items={[
                ...(isGroupOrganisation
                  ? [
                      {
                        key: 'group-details',
                        label: t('group.group-details'),
                        children: (
                          <GroupConnectedEntitiesList
                            members={groupDetailMembers}
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  key: 'basic',
                  label: t('organisation.basic-details'),
                  children: (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '30px 20px',
                      }}
                    >
                      <div style={{ gridColumn: 1 }}>
                        {renderFormField(mainFields[0])}
                      </div>
                      <div style={{ gridColumn: 2 }}>
                        {renderFormField(mainFields[1])}
                      </div>
                      <div style={{ gridColumn: 3 }} />
                      <div style={{ gridColumn: 1 }}>
                        {renderFormField(mainFields[2])}
                      </div>
                      <div style={{ gridColumn: 2 }}>
                        {renderFormField(mainFields[3])}
                      </div>
                      <div style={{ gridColumn: 3 }}>
                        {renderFormField(mainFields[4])}
                      </div>
                      <div
                        className="edit-details-notes-field"
                        style={{ gridColumn: '1 / span 3' }}
                      >
                        <LabelWrapper>
                          <span className="label-text">
                            {t('organisation.additional-notes')}
                            {!isReceiptsNotesRequired && (
                              <>
                                {' '}
                                <span className="notes-label-optional">
                                  {t('organisation.optional')}
                                </span>
                              </>
                            )}
                            {isReceiptsNotesRequired && (
                              <span style={{ color: '#D13131' }}> *</span>
                            )}
                          </span>
                        </LabelWrapper>
                        {isReceiptsNotesRequired && (
                          <p
                            style={{
                              marginBottom: 8,
                              marginTop: 0,
                              fontSize: 12,
                              color: '#595959',
                              lineHeight: 1.4,
                              whiteSpace: 'pre-line',
                            }}
                          >
                            {t('organisation.notes-receipts-help')}
                          </p>
                        )}
                        <Form.Item
                          name="notes"
                          style={{ marginBottom: 0 }}
                          rules={
                            isReceiptsNotesRequired
                              ? [
                                  {
                                    required: true,
                                    message: t('common.required-field'),
                                  },
                                ]
                              : undefined
                          }
                        >
                          <FormTextArea
                            rows={4}
                            maxLength={ORGANISATION_NOTES_MAX_LENGTH}
                            value={orgValues?.notes ?? ''}
                            onChange={(e) =>
                              handleFieldChange(
                                'notes',
                                e.target.value.slice(
                                  0,
                                  ORGANISATION_NOTES_MAX_LENGTH
                                )
                              )
                            }
                          />
                        </Form.Item>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: 4,
                            fontSize: 12,
                            color: 'var(--color-text-muted, #5a6a6a)',
                          }}
                        >
                          <span>
                            {t('organisation.notes-max-characters', {
                              max: ORGANISATION_NOTES_MAX_LENGTH,
                            })}
                          </span>
                          <span>
                            {(orgValues?.notes ?? '').length}/
                            {ORGANISATION_NOTES_MAX_LENGTH}
                          </span>
                        </div>
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'more',
                  label: t('organisation.more-details'),
                  children: (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '30px 20px',
                      }}
                    >
                      {moreDetailsFields.map(renderFormField)}
                    </div>
                  ),
                },
                ...(!isAddMode
                  ? [
                      {
                        key: 'attachments',
                        label: t('organisation.attachments-title'),
                        children: (
                          <>
                            <p
                              style={{
                                fontFamily: 'Inter',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '22px',
                                color: 'var(--color-text-muted, #5a6a6a)',
                                marginBottom: 20,
                              }}
                            >
                              {t('organisation.attachments-description')}
                            </p>
                            <Spin spinning={loading}>
                              {organisationDetails.attachments &&
                                organisationDetails.attachments.length > 0 &&
                                organisationDetails.attachments.map(
                                  (attachment) => (
                                    <Space
                                      key={attachment.uuid}
                                      style={{
                                        borderBottom: '1px solid var(--color-border, #dbeae3)',
                                        padding: '8px 0px',
                                      }}
                                    >
                                      <Space
                                        direction="vertical"
                                        style={{ rowGap: '0', minWidth: 680 }}
                                      >
                                        <BoldNameSpan
                                          style={{
                                            fontSize: 13,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {attachment.description} -{' '}
                                          {attachment.fileName}
                                        </BoldNameSpan>
                                        <BoldNameSpan
                                          fontWeight={'400'}
                                          style={{ color: 'var(--color-text-muted, #5a6a6a)' }}
                                        >
                                          Uploaded on{' '}
                                          {formatDate(
                                            attachment.generationDate
                                          )}
                                        </BoldNameSpan>
                                      </Space>
                                      <StyledPopconfirm
                                        title={t(
                                          'organisation.remove-attachment-title'
                                        )}
                                        description={t(
                                          'organisation.remove-attachment-description'
                                        )}
                                        onConfirm={() => {
                                          dispatch(
                                            deleteOrganisationAttachmentRequest(
                                              organisationId,
                                              attachment.uuid
                                            )
                                          );
                                        }}
                                        placement="left"
                                        okText={t('common.yes')}
                                        cancelText={t('common.cancel')}
                                        overlayStyle={{
                                          width: '400px',
                                          maxWidth: '400px',
                                        }}
                                      >
                                        <Button
                                          type="link"
                                          shape="circle"
                                          icon={<DeleteOutlined />}
                                          aria-label={t('common.delete')}
                                        />
                                      </StyledPopconfirm>
                                    </Space>
                                  )
                                )}
                            </Spin>
                            {(!organisationDetails.attachments ||
                              organisationDetails.attachments.length < 3) && (
                              <div
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  marginTop: 30,
                                }}
                              >
                                <ScopeButton
                                  type="primary"
                                  width="320px"
                                  height="40px"
                                  onClick={handleAddAttachment}
                                >
                                  {t('organisation.add-attachment')}
                                </ScopeButton>
                              </div>
                            )}
                          </>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </EditDetailsTabsContainer>
          {(isAddMode ||
            (activeTabKey !== 'attachments' &&
              activeTabKey !== 'group-details')) && (
            <Space
              align="center"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                margin: '30px 0px',
              }}
            >
              <ScopeButton
                type="primary"
                width="320px"
                height="40px"
                loading={loading}
                disabled={loading}
                onClick={() => {
                  form
                    .validateFields()
                    .then(() => {
                      // Strip non-editable fields (like attachments, totalTaxBalance)
                      // before sending the payload to the API.
                      const { attachments, totalTaxBalance, ...safeValues } =
                        orgValues as EditOrganisationParams & {
                          attachments?: unknown;
                          totalTaxBalance?: unknown;
                        };
                      const notes = safeValues.notes ?? '';
                      if (!isOrganisationNotesWithinLimit(notes)) {
                        dispatch(
                          turnMessageOn({
                            title: t(
                              'organisation.edit-details-notes-too-long-title'
                            ),
                            description: t(
                              'organisation.notes-too-long-description',
                              {
                                max: ORGANISATION_NOTES_MAX_LENGTH,
                                count: notes.length,
                              }
                            ),
                            type: MessageStates.WARNING,
                          } as MessageData)
                        );
                        return;
                      }
                      editOrganisationName(safeValues);
                    })
                    .catch(() => {
                      // validation errors are displayed by Form.Item
                    });
                }}
              >
                {t('organisation.save-details')}
              </ScopeButton>
            </Space>
          )}
        </Form>
        {!isAddMode && (
          <StyledModal
            title={t('organisation.add-attachment')}
            open={isUploadModalVisible}
            onCancel={handleUploadModalClose}
            centered
            footer={
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '0 0 8px',
                }}
              >
                <ScopeButton
                  type="default"
                  width="140px"
                  height="40px"
                  onClick={handleUploadModalClose}
                >
                  {t('common.cancel')}
                </ScopeButton>
                <ScopeButton
                  type="primary"
                  width="140px"
                  height="40px"
                  onClick={handleUploadConfirm}
                  disabled={!selectedFile || !fileDescription.trim()}
                >
                  {t('common.upload')}
                </ScopeButton>
              </div>
            }
          >
            <Space direction="vertical" style={{ width: '100%', gap: '20px' }}>
              <div>
                <Upload
                  beforeUpload={handleFileChange}
                  maxCount={1}
                  onRemove={() => setSelectedFile(null)}
                  accept={ORGANISATION_ATTACHMENT_ACCEPT}
                  fileList={
                    selectedFile
                      ? [
                          {
                            uid: '-1',
                            name: selectedFile.name,
                            status: 'done',
                          } as any,
                        ]
                      : []
                  }
                >
                  <ScopeButton
                    type="dark-orange"
                    width="100%"
                    height="35px"
                    icon={<UploadOutlined />}
                    disabled={!!selectedFile}
                  >
                    {t('organisation.click-to-upload')}
                  </ScopeButton>
                </Upload>
              </div>
              <div>
                <LabelWrapper>
                  <span className="label-text">
                    {t('organisation.file-description')}
                  </span>
                </LabelWrapper>
                <FormInput
                  width="100%"
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  placeholder={t('organisation.enter-file-description')}
                  border_color="var(--color-border, #dbeae3)"
                />
              </div>
            </Space>
          </StyledModal>
        )}
      </Space>
    </>
  );
};


